import { type ElementInfo, type Participant, RemoteVideoTrack, type Room, Track } from 'livekit-client';

import { participantName } from '../call/participant-name';
import { fitRect, gridLayout, type Rect, stageLayout, stripCapacity } from './layout';
import { i18n } from '@/i18n';
import { getAvatarColor, getInitials } from '@/shared/avatar';

const WIDTH = 1280;
const HEIGHT = 720;
const FRAME_RATE = 24;
const GAP = 12;
const PADDING = 16;
const MAX_GRID_TILES = 9;

type Tile = { participant: Participant; source: Track.Source; track?: MediaStreamTrack; remote?: RemoteVideoTrack };

type VideoSource = {
    element: HTMLVideoElement;
    remote?: RemoteVideoTrack;
    info?: ElementInfo;
    size: { width: number; height: number };
};

type Palette = { background: string; tile: string; border: string; speaking: string; text: string; font: string };

export type MeetingCompositor = { stream: MediaStream; stop: () => void };

function readPalette(): Palette {
    const style = getComputedStyle(document.documentElement);
    const token = (name: string) => style.getPropertyValue(name).trim();
    return {
        background: token('--color-background-default'),
        tile: token('--color-background-secondary'),
        border: token('--color-border-default'),
        speaking: token('--color-action-default'),
        text: token('--color-foreground-foreground'),
        font: getComputedStyle(document.body).fontFamily,
    };
}

function startWorkerTicker(intervalMs: number, onTick: () => void): () => void {
    const worker = new Worker(new URL('../background/frame-ticker-worker.ts', import.meta.url), { type: 'module' });
    worker.addEventListener('message', onTick);
    worker.postMessage(intervalMs);
    return () => {
        worker.terminate();
    };
}

function participantsOf(room: Room): Participant[] {
    return [room.localParticipant, ...room.remoteParticipants.values()];
}

function liveVideoTrack(participant: Participant, source: Track.Source) {
    const publication = participant.getTrackPublication(source);
    const track = publication?.track;
    if (!track || publication.isMuted || track.mediaStreamTrack.readyState !== 'live') return undefined;
    return track;
}

function collectTiles(room: Room): { screen?: Tile; cameras: Tile[] } {
    const participants = participantsOf(room);
    let screen: Tile | undefined;
    for (const participant of participants) {
        const track = liveVideoTrack(participant, Track.Source.ScreenShare);
        if (track) {
            screen = {
                participant,
                source: Track.Source.ScreenShare,
                track: track.mediaStreamTrack,
                remote: track instanceof RemoteVideoTrack ? track : undefined,
            };
        }
    }
    const cameras = participants.map((participant): Tile => {
        const track = liveVideoTrack(participant, Track.Source.Camera);
        return {
            participant,
            source: Track.Source.Camera,
            track: track?.mediaStreamTrack,
            remote: track instanceof RemoteVideoTrack ? track : undefined,
        };
    });
    return { screen, cameras };
}

function pickVisible(tiles: Tile[], limit: number): Tile[] {
    if (tiles.length <= limit) return tiles;
    const recent = new Set(
        tiles
            .toSorted(
                (a, b) =>
                    Number(b.participant.isSpeaking) - Number(a.participant.isSpeaking) ||
                    (b.participant.lastSpokeAt?.getTime() ?? 0) - (a.participant.lastSpokeAt?.getTime() ?? 0)
            )
            .slice(0, limit)
    );
    return tiles.filter((tile) => recent.has(tile));
}

export function createMeetingCompositor(room: Room): MeetingCompositor {
    const palette = readPalette();
    const canvas = document.createElement('canvas');
    canvas.width = WIDTH;
    canvas.height = HEIGHT;
    const context = canvas.getContext('2d');
    if (!context) throw new Error('Canvas 2D is unavailable');

    const audioContext = new AudioContext();
    const audioDestination = audioContext.createMediaStreamDestination();
    const audioSources = new Map<string, MediaStreamAudioSourceNode>();
    const videoSources = new Map<string, VideoSource>();

    const syncAudio = () => {
        const live = new Map<string, MediaStreamTrack>();
        for (const participant of participantsOf(room)) {
            for (const publication of participant.audioTrackPublications.values()) {
                const track = publication.track?.mediaStreamTrack;
                if (track?.readyState === 'live') live.set(track.id, track);
            }
        }
        for (const [id, node] of audioSources) {
            if (live.has(id)) continue;
            node.disconnect();
            audioSources.delete(id);
        }
        for (const [id, track] of live) {
            if (audioSources.has(id)) continue;
            const node = audioContext.createMediaStreamSource(new MediaStream([track]));
            node.connect(audioDestination);
            audioSources.set(id, node);
        }
        if (audioContext.state === 'suspended') void audioContext.resume();
    };

    const releaseVideo = (id: string, source: VideoSource) => {
        if (source.remote && source.info) source.remote.stopObservingElementInfo(source.info);
        source.element.pause();
        source.element.srcObject = null;
        videoSources.delete(id);
    };

    const videoFor = (tile: Tile, rect: Rect): HTMLVideoElement | undefined => {
        const track = tile.track;
        if (!track) return undefined;
        let source = videoSources.get(track.id);
        if (!source) {
            const element = document.createElement('video');
            element.muted = true;
            element.playsInline = true;
            element.srcObject = new MediaStream([track]);
            void element.play().catch(() => undefined);
            source = { element, remote: tile.remote, size: { width: rect.width, height: rect.height } };
            if (tile.remote) {
                const size = source.size;
                source.info = {
                    element,
                    width: () => size.width,
                    height: () => size.height,
                    visible: true,
                    pictureInPicture: true,
                    visibilityChangedAt: 0,
                    observe: () => undefined,
                    stopObserving: () => undefined,
                };
                tile.remote.observeElementInfo(source.info);
            }
            videoSources.set(track.id, source);
        } else if (source.size.width !== rect.width || source.size.height !== rect.height) {
            source.size.width = rect.width;
            source.size.height = rect.height;
            source.info?.handleResize?.();
        }
        return source.element;
    };

    const drawTile = (tile: Tile, rect: Rect, used: Set<string>) => {
        const radius = Math.min(rect.width, rect.height) * 0.04;
        const mode = tile.source === Track.Source.ScreenShare ? 'contain' : 'cover';
        context.save();
        context.beginPath();
        context.roundRect(rect.x, rect.y, rect.width, rect.height, radius);
        context.fillStyle = mode === 'contain' ? palette.background : palette.tile;
        context.fill();
        context.clip();

        const element = videoFor(tile, rect);
        if (tile.track) used.add(tile.track.id);
        const name = participantName(tile.participant.name, tile.participant.identity);
        if (element && element.readyState >= HTMLMediaElement.HAVE_CURRENT_DATA && element.videoWidth > 0) {
            const target = fitRect({ width: element.videoWidth, height: element.videoHeight }, rect, mode);
            context.drawImage(element, target.x, target.y, target.width, target.height);
        } else {
            const avatarRadius = Math.min(rect.width, rect.height) * 0.2;
            const centerX = rect.x + rect.width / 2;
            const centerY = rect.y + rect.height / 2;
            context.beginPath();
            context.arc(centerX, centerY, avatarRadius, 0, Math.PI * 2);
            context.fillStyle = getAvatarColor(name);
            context.fill();
            context.fillStyle = palette.text;
            context.font = `600 ${String(Math.round(avatarRadius * 0.8))}px ${palette.font}`;
            context.textAlign = 'center';
            context.textBaseline = 'middle';
            context.fillText(getInitials(name), centerX, centerY);
        }

        const labelSize = Math.max(11, Math.round(Math.min(rect.height * 0.07, 18)));
        context.font = `500 ${String(labelSize)}px ${palette.font}`;
        context.textAlign = 'left';
        context.textBaseline = 'middle';
        const label = tile.source === Track.Source.ScreenShare ? i18n.t('conference:tile.screenShare', { name }) : name;
        const labelPadding = labelSize * 0.6;
        const maxLabelWidth = rect.width - labelPadding * 4;
        const labelWidth = Math.min(context.measureText(label).width, maxLabelWidth) + labelPadding * 2;
        const labelHeight = labelSize * 1.8;
        const labelX = rect.x + labelPadding;
        const labelY = rect.y + rect.height - labelHeight - labelPadding;
        context.globalAlpha = 0.72;
        context.fillStyle = palette.background;
        context.beginPath();
        context.roundRect(labelX, labelY, labelWidth, labelHeight, labelHeight / 2);
        context.fill();
        context.globalAlpha = 1;
        context.fillStyle = palette.text;
        context.fillText(label, labelX + labelPadding, labelY + labelHeight / 2, maxLabelWidth);
        context.restore();

        context.beginPath();
        context.roundRect(rect.x, rect.y, rect.width, rect.height, radius);
        const speaking = tile.source === Track.Source.Camera && tile.participant.isSpeaking;
        context.lineWidth = speaking ? 4 : 1;
        context.strokeStyle = speaking ? palette.speaking : palette.border;
        context.stroke();
    };

    const drawFrame = () => {
        syncAudio();
        context.fillStyle = palette.background;
        context.fillRect(0, 0, WIDTH, HEIGHT);
        const area = { x: PADDING, y: PADDING, width: WIDTH - PADDING * 2, height: HEIGHT - PADDING * 2 };
        const { screen, cameras } = collectTiles(room);
        const used = new Set<string>();
        if (screen) {
            const visible = pickVisible(cameras, stripCapacity(area, GAP));
            const { stage, strip } = stageLayout(visible.length, area, GAP);
            drawTile(screen, stage, used);
            for (const [index, rect] of strip.entries()) {
                const tile = visible[index];
                if (tile) drawTile(tile, rect, used);
            }
        } else {
            const visible = pickVisible(cameras, MAX_GRID_TILES);
            for (const [index, rect] of gridLayout(visible.length, area, GAP).entries()) {
                const tile = visible[index];
                if (tile) drawTile(tile, rect, used);
            }
        }
        for (const [id, source] of videoSources) {
            if (!used.has(id)) releaseVideo(id, source);
        }
    };

    drawFrame();
    const stopTicker = startWorkerTicker(1000 / FRAME_RATE, drawFrame);
    const [videoTrack] = canvas.captureStream(FRAME_RATE).getVideoTracks();
    const [audioTrack] = audioDestination.stream.getAudioTracks();
    const stream = new MediaStream([videoTrack, audioTrack].filter((track) => track !== undefined));

    return {
        stream,
        stop: () => {
            stopTicker();
            for (const track of stream.getTracks()) track.stop();
            for (const [id, source] of videoSources) releaseVideo(id, source);
            for (const node of audioSources.values()) node.disconnect();
            audioSources.clear();
            void audioContext.close();
        },
    };
}
